import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Text,
  UnorderedList,
  ListItem,
  Modal,
  ModalBody,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  CloseButton,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Link,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
} from '@chakra-ui/react'
import {ArrowBackIcon} from '@chakra-ui/icons'
import {useTranslation, Trans} from 'react-i18next'
import oAuthLoginRedirect from '../../../../../../utils/oAuthLoginRedirect'
import {CheckeredShieldIcon} from '../../../../../icons'
import ConnectionReasons from './ConnectionReasons'

const AccountIntegrationConnect = ({
  accountingProvider,
  goToPreviousPage,
  signupProduct,
}) => {
  const [displayTermsModal, toggleDisplayTermsModal] = React.useState(false)
  const {t} = useTranslation('onboarding')

  return (
    <>
      <Button
        leftIcon={<ArrowBackIcon />}
        mb="lg"
        variant="tertiary"
        size="sm"
        ml="-sm"
        onClick={goToPreviousPage}
      >
        {t('onboarding:AccountIntegrationConnect.backButton')}
      </Button>
      <Text
        color="mld.neutral.900"
        fontSize="48px"
        letterSpacing="-0.5"
        lineHeight="1"
        fontWeight="400"
        mb="md"
      >
        {t('onboarding:AccountIntegrationConnect.heading', {
          accountingProvider,
        })}
      </Text>

      <Text mb="md" textStyle="body-intro">
        <Trans
          t={t}
          i18nKey="onboarding:AccountIntegrationConnect.timeNeeded"
        />
      </Text>
      <Text mb="xs" textStyle="body-intro">
        <Trans t={t} i18nKey="onboarding:AccountIntegrationConnect.body" />
      </Text>
      <ConnectionReasons
        accountingProvider={accountingProvider}
        signupProduct={signupProduct}
      />

      <Accordion allowMultiple pb="md" ml="-sm">
        <AccordionItem border="0px">
          <h2>
            <AccordionButton borderRadius="md">
              <CheckeredShieldIcon boxSize="24px" mr="sm" />
              <Box flex="1" textAlign="left">
                <Text textStyle="body-copy">
                  {t(
                    'onboarding:AccountIntegrationConnect.dataSecurityAccordion.buttonLabel',
                  )}
                </Text>
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb="0px">
            <Text textStyle="body-small" py="sm">
              {t(
                'onboarding:AccountIntegrationConnect.dataSecurityAccordion.body',
              )}
            </Text>

            <Text textStyle="body-small">
              {t(
                'onboarding:AccountIntegrationConnect.dataSecurityAccordion.notes.title',
              )}
            </Text>
            <UnorderedList pl="xs">
              <ListItem>
                <Text textStyle="body-small">
                  {t(
                    'onboarding:AccountIntegrationConnect.dataSecurityAccordion.notes.firstNote',
                  )}
                </Text>
              </ListItem>
              <ListItem>
                <Text textStyle="body-small">
                  {t(
                    'onboarding:AccountIntegrationConnect.dataSecurityAccordion.notes.secondNote',
                  )}
                </Text>
              </ListItem>
              <ListItem>
                <Text textStyle="body-small">
                  {t(
                    'onboarding:AccountIntegrationConnect.dataSecurityAccordion.notes.thirdNote',
                  )}
                </Text>
              </ListItem>
            </UnorderedList>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
      <Box mb="md" display="flex" width="100%" justifyContent="space-between">
        <Text textStyle="body-copy" flexGrow="1">
          <Trans
            t={t}
            i18nKey="onboarding:AccountIntegrationConnect.agreedOnTerm"
            values={{
              accountingProvider,
            }}
            components={{
              /* eslint-disable jsx-a11y/anchor-has-content, jsx-a11y/control-has-associated-label */
              privacyPolicyLink: (
                <Link
                  href="https://www.withmarmalade.com.au/privacy-policy"
                  target="_blank"
                  variant="primary"
                  textStyle="body-copy"
                  rel="noreferrer"
                />
              ),
            }}
          />
        </Text>
      </Box>

      <Box mt={4}>
        <Popover trigger="hover" placement="bottom">
          <PopoverTrigger>
            <Button
              variant="primary"
              colorScheme="primary"
              data-testid="continue-button"
              size="lg"
              onClick={() => {
                oAuthLoginRedirect(accountingProvider)
              }}
            >
              {t('onboarding:AccountIntegrationConnect.submitButton', {
                accountingProvider,
              })}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            bg="mld.neutral.1000"
            width="fit-content"
            boxShadow="lg"
            p="xs"
          >
            <PopoverArrow bg="mld.neutral.1000" />
            <PopoverBody fontSize="xs" color="mld.neutral.0">
              <Text textStyle="body-small">
                {t('onboarding:AccountIntegrationConnect.submitButtonTooltip', {
                  accountingProvider,
                })}
              </Text>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Box>

      <Modal
        isOpen={displayTermsModal}
        onClose={() => toggleDisplayTermsModal(false)}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent borderRadius="lg">
          <ModalHeader
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Text fontSize="lg" fontWeight="medium">
              {t('onboarding:AccountIntegrationConnect.terms.heading')}
            </Text>
            <CloseButton
              onClick={() => toggleDisplayTermsModal(false)}
              fontSize="10px"
              flexGrow="0"
              color="neutral.black"
              backgroundColor="transparent"
              _hover={{backgroundColor: 'neutral.smoke'}}
              _focus={{boxShadow: '0 0 0 3px rgba(73,70,233,0.25)'}}
            />
          </ModalHeader>
          <ModalBody>
            <Text mb="sm">
              {t('onboarding:AccountIntegrationConnect.terms.list.heading', {
                accountingProvider,
              })}
            </Text>
            <UnorderedList mb="sm">
              <ListItem mb="sm">
                {t('onboarding:AccountIntegrationConnect.terms.list.item1')}
              </ListItem>
              <ListItem mb="sm">
                {t('onboarding:AccountIntegrationConnect.terms.list.item2')}
              </ListItem>
              <ListItem mb="sm">
                {t('onboarding:AccountIntegrationConnect.terms.list.item3')}
              </ListItem>
              <ListItem mb="sm">
                {t('onboarding:AccountIntegrationConnect.terms.list.item4')}
              </ListItem>
            </UnorderedList>
            <Text mb="sm">
              {t('onboarding:AccountIntegrationConnect.terms.body1', {
                accountingProvider,
              })}
            </Text>
            <Text mb="sm">
              {t('onboarding:AccountIntegrationConnect.terms.body2')}
            </Text>
            <Text mb="sm">
              {t('onboarding:AccountIntegrationConnect.terms.body3')}
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

AccountIntegrationConnect.propTypes = {
  accountingProvider: PropTypes.string.isRequired,
  goToPreviousPage: PropTypes.func.isRequired,
  signupProduct: PropTypes.oneOf(['points_on_payments', 'payments_on_demand'])
    .isRequired,
}

export default AccountIntegrationConnect
